import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, isProviderMaintenanceError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Download, Upload, KeyRound, X, WalletCards, Gift } from "lucide-react";
import { toast } from "sonner";

/**
 * Three player actions inside the game page:
 *   - LOAD: add dollars to your game balance
 *   - REDEEM: ask operator to cash out dollars
 *   - RESET PASSWORD: ask operator to issue a new game password
 */
const GAME_LOAD_MIN_AMOUNT = 5;
const GAME_LOAD_MAX_EXISTING_BALANCE = 5;

function parseGameBalance(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const clean = String(value).replace(/[$,]/g, "").trim();
  const direct = Number(clean);
  if (Number.isFinite(direct)) return direct;
  const match = clean.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function GameActionsBar({
  gameId,
  game,
  onRequestCreated,
  onLoadComplete,
  onNeedAccount,
  automationConnected = false,
  automationSupported = false,
  hasGameAccount = false,
  providerBalance = null,
  providerRedeemableBalance = null,
  providerBalanceLoaded = false,
}) {
  const [open, setOpen] = useState(null); // "load" | "redeem" | "reset" | null
  const { user } = useAuth();
  const nav = useNavigate();
  const numericProviderBalance = parseGameBalance(providerBalance);
  const loadBlockedByGameBalance =
    automationConnected &&
    providerBalanceLoaded &&
    numericProviderBalance !== null &&
    numericProviderBalance >= GAME_LOAD_MAX_EXISTING_BALANCE;

  const openAction = (action) => {
    if (!user || user === false) {
      toast.info("Please sign in again.");
      nav("/login");
      return;
    }
    if (action === "load" && loadBlockedByGameBalance) {
      toast.info(`Load is available only when game balance is under $${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)}.`);
      return;
    }
    if ((action === "load" || action === "redeem") && !automationConnected) {
      toast.error("Automatic backend is not connected for this game. Attach the provider session first.");
      return;
    }
    if (action === "load" && !hasGameAccount) {
      if (onNeedAccount) {
        onNeedAccount();
      } else {
        toast.info(automationSupported ? "Request this game account first, then load." : "This game needs an approved account before loading.");
      }
      return;
    }
    setOpen(action);
  };

  return (
    <>
      {loadBlockedByGameBalance && (
        <div className="mt-6 rounded-2xl border border-amber-500/25 bg-amber-900/15 px-4 py-3 text-sm text-amber-200">
          Load is hidden because this game account has ${numericProviderBalance.toFixed(2)} inside the game. Load again after the game balance is under ${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)}.
        </div>
      )}
      <div className={`mt-6 grid grid-cols-1 gap-3 ${loadBlockedByGameBalance ? "sm:grid-cols-2" : "sm:grid-cols-3"}`} data-testid="game-actions-bar">
        {!loadBlockedByGameBalance && (
          <ActionPill
            icon={Download}
            label="Load"
            sub="Add dollars"
            color="emerald"
            testid="action-load"
            onClick={() => openAction("load")}
          />
        )}
        <ActionPill
          icon={Upload}
          label="Redeem"
          sub="Cash out"
          color="gold"
          testid="action-redeem"
          onClick={() => openAction("redeem")}
        />
        <ActionPill
          icon={KeyRound}
          label="Reset password"
          sub="Admin approval"
          color="slate"
          testid="action-reset"
          onClick={() => openAction("reset")}
        />
      </div>

      {open === "load" && (
        <LoadDialog
          gameId={gameId}
          game={game}
          onLoadComplete={onLoadComplete}
          onClose={() => setOpen(null)}
          automationConnected={automationConnected}
          loadBlockedByGameBalance={loadBlockedByGameBalance}
          providerBalance={providerBalance}
        />
      )}
      {open === "redeem" && (
        <RedeemDialog
          gameId={gameId}
          game={game}
          onLoadComplete={onLoadComplete}
          onClose={() => setOpen(null)}
          providerBalance={providerBalance}
          providerRedeemableBalance={providerRedeemableBalance}
          providerBalanceLoaded={providerBalanceLoaded}
        />
      )}
      {open === "reset" && <ResetPasswordDialog gameId={gameId} onRequestCreated={onRequestCreated} onClose={() => setOpen(null)} />}
    </>
  );
}

function ActionPill({ icon: Icon, label, sub, color, onClick, testid }) {
  const tones = {
    emerald: "border-emerald-500/30 hover:border-emerald-400/60 bg-emerald-900/10 hover:bg-emerald-900/20 text-emerald-300",
    gold: "border-[#D4AF37]/40 hover:border-[#D4AF37]/80 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/15 text-[#D4AF37]",
    slate: "border-white/15 hover:border-white/30 bg-white/[0.03] hover:bg-white/[0.06] text-white/80",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group flex min-w-0 items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all hover:-translate-y-0.5 ${tones[color]}`}
    >
      <div className="w-10 h-10 rounded-lg bg-black/40 border border-white/10 grid place-items-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 text-left">
        <div className="font-display font-bold text-base leading-tight break-words">{label}</div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-white/40 break-words">{sub}</div>
      </div>
    </button>
  );
}

/* ---------- Dialog shell ---------- */
function Dialog({ title, subtitle, onClose, children, testid }) {
  return (
    <div
      className="fixed inset-0 z-[120] overflow-y-auto overscroll-contain bg-black/70 p-3 py-5 pb-[calc(7rem+env(safe-area-inset-bottom))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
        className="mx-auto w-full max-w-md rounded-2xl border border-[#D4AF37]/25 bg-[#0A130E] p-6 shadow-2xl sm:p-7"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="font-display text-xl font-bold leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-white/50 mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function loadUsageForSource(source, amount, walletDollars, freePlayDollars) {
  const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 0;
  if (source === "wallet") {
    return {
      available: Math.max(0, walletDollars),
      wallet: safeAmount,
      freeplay: 0,
      label: "wallet only",
    };
  }
  if (source === "freeplay") {
    return {
      available: Math.max(0, freePlayDollars),
      wallet: 0,
      freeplay: safeAmount,
      label: "freeplay only",
    };
  }
  const freeplay = Math.min(safeAmount, Math.max(0, freePlayDollars));
  return {
    available: Math.max(0, walletDollars) + Math.max(0, freePlayDollars),
    wallet: Math.max(0, safeAmount - freeplay),
    freeplay,
    label: "freeplay first",
  };
}

/* ---------- LOAD ---------- */
function LoadDialog({ gameId, game, onLoadComplete, onClose, automationConnected = false, loadBlockedByGameBalance = false, providerBalance = null }) {
  const { user, refreshUser } = useAuth();
  const [amount, setAmount] = useState(10);
  const [fundSource, setFundSource] = useState("wallet");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [retrySeconds, setRetrySeconds] = useState(0);
  useEffect(() => {
    if (retrySeconds <= 0) return undefined;
    const timer = window.setTimeout(() => setRetrySeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [retrySeconds]);
  const walletDollars = (Number(user?.coins || 0) / 1000);
  const freePlayDollars = (Number(user?.free_play || 0) / 1000);
  const availableDollars = walletDollars + freePlayDollars;
  const loadDollars = Number(amount || 0);
  const walletAmount = Math.round(loadDollars * 1000);
  const firstBonus = Number(game?.first_deposit_bonus_percent ?? 100);
  const regularBonus = Number(game?.regular_deposit_bonus_percent ?? 20);
  const currentBonus = Number(game?.current_bonus_percent ?? regularBonus);
  const loadAmountForSource = (source, preferFull = false) => {
    const sourceAvailable = source === "wallet"
      ? walletDollars
      : source === "freeplay"
      ? freePlayDollars
      : availableDollars;
    const wholeAvailable = Math.floor(Math.max(0, sourceAvailable));
    if (wholeAvailable < GAME_LOAD_MIN_AMOUNT) return amount;
    const currentAmount = Number(amount || 0);
    if (!preferFull && Number.isFinite(currentAmount) && currentAmount >= GAME_LOAD_MIN_AMOUNT && currentAmount <= wholeAvailable) {
      return currentAmount;
    }
    return wholeAvailable;
  };
  const selectFundSource = (source, preferFull = false) => {
    setFundSource(source);
    setAmount(loadAmountForSource(source, preferFull));
  };
  useEffect(() => {
    if (fundSource === "wallet" && walletDollars <= 0 && freePlayDollars > 0) {
      setFundSource("freeplay");
      setAmount((current) => {
        const currentAmount = Number(current || 0);
        const maxFreeplay = Math.floor(Math.max(0, freePlayDollars));
        return maxFreeplay >= GAME_LOAD_MIN_AMOUNT && (!Number.isFinite(currentAmount) || currentAmount > maxFreeplay)
          ? maxFreeplay
          : current;
      });
    }
    if (fundSource === "freeplay" && freePlayDollars <= 0 && walletDollars > 0) setFundSource("wallet");
  }, [fundSource, walletDollars, freePlayDollars]);
  const selectedUsage = loadUsageForSource(fundSource, loadDollars, walletDollars, freePlayDollars);
  const projectedFreePlayDollars = selectedUsage.freeplay;
  const projectedCashDollars = selectedUsage.wallet;
  const projectedBonusDollars = projectedCashDollars > 0 && currentBonus > 0
    ? Math.round(projectedCashDollars * currentBonus) / 100
    : 0;
  const projectedGameLoadDollars = loadDollars + projectedBonusDollars;

  const submit = async (e) => {
    e.preventDefault();
    if (loading || retrySeconds > 0) return;
    setErr(""); setLoading(true);
    try {
      if (!Number.isFinite(loadDollars) || loadDollars <= 0) {
        throw new Error("Enter a valid dollar amount.");
      }
      if (loadDollars < GAME_LOAD_MIN_AMOUNT) {
        throw new Error(`Minimum game load is $${GAME_LOAD_MIN_AMOUNT.toFixed(2)}.`);
      }
      if (loadBlockedByGameBalance) {
        const shownBalance = parseGameBalance(providerBalance);
        throw new Error(
          shownBalance !== null
            ? `Load is available only when game balance is under $${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)}. Current game balance is $${shownBalance.toFixed(2)}.`
            : `Load is available only when game balance is under $${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)}.`,
        );
      }
      if (loadDollars > availableDollars) {
        throw new Error("Load amount is higher than your wallet + freeplay balance.");
      }
      if (loadDollars > selectedUsage.available) {
        const sourceLabel = fundSource === "wallet" ? "wallet balance" : fundSource === "freeplay" ? "freeplay balance" : "wallet + freeplay balance";
        throw new Error(`Load amount is higher than your selected ${sourceLabel}.`);
      }
      const endpoint = automationConnected ? "/automation/load" : "/automation/manual-load-request";
      const { data } = await api.post(endpoint, {
        gameName: gameId,
        amount: Math.round(loadDollars),
        walletAmount,
        fundSource,
        remark: automationConnected ? "website game load" : "manual backend game load request",
      });
      await refreshUser?.();
      await onLoadComplete?.(data);
      const bonus = Number(data?.bonus_amount || 0);
      const loaded = Number(data?.provider_amount || data?.amount || loadDollars);
      const manualReview = data?.source === "manual-review" || data?.status === "pending_manual_review";
      if (automationConnected && !manualReview) {
        toast.success(`Loaded ${loaded.toLocaleString("en-US")} to game${bonus ? ` (${bonus.toLocaleString("en-US")} bonus)` : ""}`);
      } else {
        toast.success(`Load request sent for ${loaded.toLocaleString("en-US")}${bonus ? ` (${bonus.toLocaleString("en-US")} bonus)` : ""}`);
      }
      onClose();
    } catch (e) {
      if (isProviderMaintenanceError(e)) setRetrySeconds(60);
      setErr(e?.message && !e?.response ? e.message : formatApiError(e, { audience: "player" }));
    } finally { setLoading(false); }
  };

  return (
    <Dialog title="Load to game" subtitle={automationConnected ? "Moves freeplay or wallet money into this game" : "Sends this game load to admin for review"} onClose={onClose} testid="load-dialog">
      {user && (
        <div className="grid grid-cols-1 gap-1 text-xs text-white/40 mb-4 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => walletDollars > 0 && selectFundSource("wallet", true)}
            disabled={walletDollars <= 0}
            className={`rounded-lg px-2 py-1 text-left transition ${walletDollars > 0 ? "hover:bg-white/5" : "cursor-default"}`}
          >
            Wallet: <span className="text-white font-medium tabular-nums">${walletDollars.toFixed(2)}</span>
          </button>
          <button
            type="button"
            onClick={() => freePlayDollars > 0 && selectFundSource("freeplay", true)}
            disabled={freePlayDollars <= 0}
            className={`rounded-lg px-2 py-1 text-left transition ${freePlayDollars > 0 ? "hover:bg-[#D4AF37]/10" : "cursor-default"}`}
          >
            Freeplay: <span className="text-[#F8E08E] font-medium tabular-nums">${freePlayDollars.toFixed(2)}</span>
          </button>
          <div>Available: <span className="text-emerald-300 font-medium tabular-nums">${availableDollars.toFixed(2)}</span></div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        {(walletDollars > 0 || freePlayDollars > 0) && (
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">Load source</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => selectFundSource("wallet", true)}
                data-testid="load-source-wallet"
                className={`flex items-center justify-between rounded-lg border px-3 py-3 text-left text-sm transition ${
                  fundSource === "wallet"
                    ? "border-sky-400 bg-sky-400/10 text-white shadow-sm"
                    : "border-white/10 bg-white/[0.03] text-white/65 hover:border-white/25"
                }`}
              >
                <span className="flex items-center gap-2 font-display font-bold"><WalletCards className="h-4 w-4" /> Wallet only</span>
                <span className="font-display font-bold tabular-nums">${walletDollars.toFixed(2)}</span>
              </button>
              <button
                type="button"
                onClick={() => selectFundSource("freeplay", true)}
                data-testid="load-source-freeplay"
                className={`flex items-center justify-between rounded-lg border px-3 py-3 text-left text-sm transition ${
                  fundSource === "freeplay"
                    ? "border-sky-400 bg-sky-400/10 text-white shadow-sm"
                    : "border-white/10 bg-white/[0.03] text-white/65 hover:border-white/25"
                }`}
              >
                <span className="flex items-center gap-2 font-display font-bold"><Gift className="h-4 w-4" /> Freeplay only</span>
                <span className="font-display font-bold tabular-nums">${freePlayDollars.toFixed(2)}</span>
              </button>
            </div>
          </div>
        )}
        <Field label="Amount ($)">
          <input
            type="number" required min={GAME_LOAD_MIN_AMOUNT} step={1} value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="load-amount"
            className={inp}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          {[5, 10, 20, 50, 100].map((v) => (
            <button key={v} type="button" onClick={() => setAmount(v)}
              data-testid={`load-quick-${v}`}
              className="px-3 py-1 text-xs rounded-full border border-white/10 hover:bg-white/5">
              ${v}
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-3 text-xs leading-relaxed text-[#F8E08E]">
          {fundSource === "freeplay"
            ? "Freeplay loads at face value only and does not receive load bonus. Freeplay redeem is $20-$50 with 40% credited to wallet and 60% deducted."
            : `Wallet loads keep Freeplay untouched. Cash wallet loads get ${firstBonus}% first bonus, then ${regularBonus}% regular bonus. Redeem minimum uses the full game load after bonus.`}
          {projectedCashDollars > 0 && (
            <span className="mt-2 block rounded-lg border border-sky-300/20 bg-sky-400/10 px-3 py-2 text-sky-100">
              This cash load gets {currentBonus}% bonus: ${projectedBonusDollars.toFixed(2)} bonus, ${projectedGameLoadDollars.toFixed(2)} total to game.
            </span>
          )}
          {!automationConnected && (
            <span className="mt-2 block text-white/80">
              This provider needs manual review right now. Your selected wallet/freeplay amount is held until admin approves or rejects the load.
            </span>
          )}
          <span className="mt-2 block text-white/80">
            This load will use {projectedFreePlayDollars > 0 ? `$${projectedFreePlayDollars.toFixed(2)} freeplay` : ""}
            {projectedFreePlayDollars > 0 && projectedCashDollars > 0 ? " and " : ""}
            {projectedCashDollars > 0 ? `$${projectedCashDollars.toFixed(2)} wallet cash` : ""}
            {projectedFreePlayDollars <= 0 && projectedCashDollars <= 0 ? selectedUsage.label : ""}.
          </span>
        </div>
        {err && <Err msg={err} />}
        <button type="submit" disabled={loading || retrySeconds > 0} data-testid="load-submit"
          className="w-full py-3 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all disabled:opacity-50">
          {retrySeconds > 0 ? `Retry in ${retrySeconds}s` : loading ? "Loading..." : automationConnected ? `Load ${fundSource === "freeplay" ? "freeplay" : fundSource === "wallet" ? "wallet" : "game"} now` : "Send load request"}
        </button>
      </form>
    </Dialog>
  );
}

/* ---------- REDEEM ---------- */
function RedeemDialog({ gameId, game, onLoadComplete, onClose, providerBalance = null, providerRedeemableBalance = null, providerBalanceLoaded = false }) {
  const { refreshUser } = useAuth();
  const [amount, setAmount] = useState(30);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const numericProviderBalance = parseGameBalance(providerBalance);
  const numericProviderRedeemableBalance = parseGameBalance(providerRedeemableBalance);
  const effectiveRedeemBalance =
    numericProviderRedeemableBalance !== null && numericProviderRedeemableBalance > 0
      ? numericProviderRedeemableBalance
      : numericProviderBalance;
  const providerWholeDollarAmount =
    providerBalanceLoaded && effectiveRedeemBalance !== null && effectiveRedeemBalance > 0
      ? Math.floor(effectiveRedeemBalance)
      : null;
  const hasFractionalProviderBalance =
    providerWholeDollarAmount !== null && effectiveRedeemBalance - providerWholeDollarAmount > 0.009;
  const hasSeparateRedeemableBalance =
    numericProviderBalance !== null &&
    numericProviderRedeemableBalance !== null &&
    Math.abs(numericProviderBalance - numericProviderRedeemableBalance) > 0.009;
  const shownProviderBalance = numericProviderBalance ?? effectiveRedeemBalance;
  const safeBalanceGame = ["vblink", "ultrapanda"].includes(String(gameId || "").toLowerCase().replace(/[^a-z0-9]/g, ""));
  useEffect(() => {
    if (providerWholeDollarAmount !== null) {
      setAmount(providerWholeDollarAmount);
    }
  }, [providerWholeDollarAmount]);
  const redeemAmount = Number(amount || 0);
  const minMultiplier = Number(game?.min_load_multiplier ?? 4);
  const maxMultiplier = Number(game?.max_load_multiplier ?? 10);
  const minRedeem = Number(game?.min_redeem ?? 20);
  const maxRedeem = Number(game?.max_redeem ?? 1000);
  const redeemFee = Number(game?.redeem_fee_percent ?? 5);
  const eligiblePreview = Number.isFinite(redeemAmount) && redeemAmount > 0
    ? Math.min(redeemAmount, maxRedeem > 0 ? maxRedeem : redeemAmount)
    : 0;
  const voidPreview = Number.isFinite(redeemAmount) && maxRedeem > 0 ? Math.max(0, redeemAmount - maxRedeem) : 0;
  const feePreview = eligiblePreview > 0 ? eligiblePreview * (redeemFee / 100) : 0;
  const walletCreditPreview = Math.max(0, eligiblePreview - feePreview);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (!Number.isFinite(redeemAmount) || redeemAmount <= 0) {
        throw new Error("Enter a valid redeem amount.");
      }
      const { data } = await api.post("/automation/redeem", {
        gameName: gameId,
        amount: Math.floor(redeemAmount),
        remark: "website game redeem",
      });
      await refreshUser?.();
      await onLoadComplete?.(data);
      const credited = Number(data?.wallet_credit_amount || 0) / 1000;
      const voided = Number(data?.voided_amount || 0);
      toast.success(
        voided > 0
          ? `Redeemed full $${Number(data?.amount || redeemAmount).toFixed(2)}. $${voided.toFixed(2)} voided by rules; credited $${credited.toFixed(2)}.`
          : `Redeemed $${Number(data?.amount || redeemAmount).toFixed(2)} from game and credited $${credited.toFixed(2)} to wallet`,
      );
      onClose();
    } catch (e) {
      setErr(e?.message && !e?.response ? e.message : formatApiError(e, { audience: "player" }));
    } finally { setLoading(false); }
  };

  return (
    <Dialog title="Redeem from game" subtitle="Redeem pulls the full game balance and credits your wallet by rules" onClose={onClose} testid="redeem-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Amount">
          <input
            type="number" required min={1} step={1} value={amount}
            readOnly={providerWholeDollarAmount !== null}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="redeem-amount"
            className={`${inp} ${providerWholeDollarAmount !== null ? "cursor-not-allowed opacity-90" : ""}`}
          />
        </Field>
        {providerWholeDollarAmount !== null ? (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-900/10 p-3 text-xs text-emerald-100">
            {hasSeparateRedeemableBalance
              ? `Current game balance is $${shownProviderBalance.toFixed(2)}. RiverSweeps redeemable win amount is $${numericProviderRedeemableBalance.toFixed(2)}. Redeem will pull $${providerWholeDollarAmount}.00 only.`
              : `Current game balance is $${shownProviderBalance.toFixed(2)}. Redeem will pull $${providerWholeDollarAmount}.00 only.`}
            {hasFractionalProviderBalance && (
              <span className="mt-1 block text-emerald-100/80">
                The ${Math.max(0, effectiveRedeemBalance - providerWholeDollarAmount).toFixed(2)} fractional points stay inside the game.
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[30, 50, 100, 200].map((v) => (
              <button key={v} type="button" onClick={() => setAmount(v)}
                data-testid={`redeem-quick-${v}`}
                className="px-3 py-1 text-xs rounded-full border border-white/10 hover:bg-white/5">
                {v}
              </button>
            ))}
          </div>
        )}
        <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-3 text-xs leading-relaxed text-[#F8E08E]">
          Cash-load redeem rule: {minMultiplier}x to {maxMultiplier}x latest cash/base load before bonus, ${minRedeem}-${maxRedeem}, {redeemFee}% fee.
          <span className="mt-1 block text-white/80">
            Redeem pulls the whole-dollar game balance. If the game balance is above the rule max, the extra amount is voided and only the allowed amount credits to wallet.
          </span>
          {safeBalanceGame && (
            <span className="mt-1 block text-white/80">
              If this game has Safe/Bank balance, move it back into the main game balance first. Redeem stops when safe balance is detected.
            </span>
          )}
          <span className="mt-1 block text-white/80">
            Example: $20 cash with a 100% bonus loads $40 into the game, but redeem limits still use the $20 cash/base load. With 3x-10x rules, redeem is $60 minimum and $200 maximum.
          </span>
          {redeemAmount > 0 && (
            <span className="mt-1 block text-white/80">
              For a cash-funded load, a ${redeemAmount.toFixed(2)} redeem deducts ${feePreview.toFixed(2)} fee and credits about ${walletCreditPreview.toFixed(2)} to wallet{voidPreview > 0 ? `; $${voidPreview.toFixed(2)} is voided by rules` : ""}.
            </span>
          )}
          <span className="mt-1 block">Freeplay loads redeem $20-$50 with 40% credited to wallet and 60% deducted, including birthday, weekly topper, and promo Freeplay. Referral bonuses go directly to your cash wallet.</span>
          {redeemAmount >= 20 && Number.isFinite(redeemAmount) && (
            <span className="mt-1 block text-white/80">
              For a Freeplay-funded load, ${(Math.min(redeemAmount, 50) * 0.4).toFixed(2)} credits to wallet and ${(Math.min(redeemAmount, 50) * 0.6).toFixed(2)} is deducted{redeemAmount > 50 ? `; $${(redeemAmount - 50).toFixed(2)} above the $50 limit is voided` : ""}.
            </span>
          )}
        </div>
        {err && <Err msg={err} />}
        <button type="submit" disabled={loading} data-testid="redeem-submit"
          className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] transition-all disabled:opacity-50">
          {loading ? "Redeeming..." : "Redeem now"}
        </button>
      </form>
    </Dialog>
  );
}

/* ---------- RESET PASSWORD ---------- */
function ResetPasswordDialog({ gameId, onRequestCreated, onClose }) {
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/me/requests", {
        type: "reset_password",
        amount: 0,
        game_id: gameId,
        note: note || "Customer requested a new game password",
      });
      onRequestCreated?.(data);
      toast.success(data?.status === "approved" ? "Password reset complete" : "Password reset is processing");
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <Dialog title="Reset password" subtitle="Auto reset sends a new password when this game is connected" onClose={onClose} testid="reset-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/60">
          You do not need the old password here. Submit once, then the new password will appear on this game page after automation finishes.
        </div>
        <Field label="Note for admin (optional)">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. I forgot my Ultra Panda password"
            data-testid="reset-note"
            className={inp}
          />
        </Field>
        {err && <Err msg={err} />}
        <button type="submit" disabled={loading} data-testid="reset-submit"
          className="w-full py-3 bg-white text-black font-bold rounded-full hover:bg-slate-200 transition-all disabled:opacity-50">
          {loading ? "Resetting..." : "Reset password now"}
        </button>
      </form>
    </Dialog>
  );
}

/* ---------- shared ---------- */
const inp = "w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</label>
      {children}
    </div>
  );
}

function Err({ msg }) {
  return <div className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">{msg}</div>;
}
